import {
  Grid,
  PromptResultsAnnouncement,
  PromptTab,
  ShareToWorkgroupDialog,
  WorkgroupPublisherTeachingPopover,
} from "@1js/pl-card-components";
import { Toaster, mergeClasses } from "@fluentui/react-components";
import * as React from "react";
import { type AllPromptsProps } from "./AllPrompts.web.data";
import { useStyles } from "./AllPrompts.web.styles";

import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import { ConditionalWrap } from "../ConditionalWrap/ConditionalWrap.web";
import { useAllPrompts } from "./AllPrompts.hook.web";
import { BookmarkPrompts } from "./BookmarkPrompts.web";
import { SuggestedPrompts } from "./SuggestedPrompts.web";
import { WorkgroupPrompts } from "./WorkgroupPrompts.web";
export const AllPrompts: React.FC<AllPromptsProps> = React.memo(
  ({
    id,
    role,
    className,
    skeletonCount,
    status,
    isShowFilters = true,
    isShowHasMore = true,
    enableCarousel = false,
    hasMore,
    hasBookmarkedPrompts,
    prompts,
    morePrompts,
    filters,
    selectedFacets,
    selectedTab,
    selectedGroupId: selectedGroupId,
    onFilterChanged,
    onPromptClicked,
    onPromptBookmarkToggled,
    onMoreClicked,
    onSharePromptClicked,
    onCopyPromptClicked,
    onBookmarkToggled,
    onPromptsTabSelected,
    onGroupPromptsFilterChanged,
    onDelete,
    onTryInAppBadgeAction,
    onShareToWorkgroup,
    groupPrompts,
    onAnnouncementSharePromptsClicked,
    onAnnouncementExploreOtherPromptsClicked,
    groupPromptStatus,
    handlePromptsTabSelected,
    notify,
    isTeachingExperiencesEnabled,
  }) => {
    const styles = useStyles();
    const {
      activePrompt,
      currentFocusedPromptId,
      dialogOpen,
      handleCardClick,
      handleCardFocus,
      handleDialogClose,
      handleFilterChange,
      handleGroupPromptsFilterOnOpenChange,
      handleShareViaCopyLinkClicked,
      handleShareViaEmailClicked,
      handleTabChange,
      isFlightActive,
      isLoading,
      isShareToWorkgroupDialogOpen,
      isWorkgroupPublishingEnabled,
      onCloseShareToWorkgroupDialog,
      promptRefs,
      promptsToDisplay,
      promptsToShareToWorkgroup,
      resultsAnnouncementId,
      setDialogOpen,
      setIsPopoverOpen,
      setLastFocusedPromptId,
      setCurrentFocusedPromptId,
      setworkgroupPublisherTeachingPopoverTarget,
      shareToWorkgroup,
      swipe,
      toasterId,
      workgroupPublisherTeachingPopoverTarget,
      onGroupDropdownOptionSelect,
      onGroupDropdownOpenChange,
    } = useAllPrompts({
      filters,
      groupPrompts,
      onFilterChanged,
      onPromptClicked,
      onPromptsTabSelected,
      prompts,
      status,
      selectedTab,
    });

    const { state } = React.useContext(LibraryContext);
    const { groups, groupHasPromptsStatus } = state;

    const announcements = (
      <PromptResultsAnnouncement
        id={resultsAnnouncementId}
        prompts={promptsToDisplay}
        isLoading={
          isLoading ||
          groupPromptStatus === "loading" ||
          groupHasPromptsStatus === "loading"
        }
        isFailed={
          status === "failed" ||
          groupPromptStatus === "failed" ||
          groupHasPromptsStatus === "failed"
        }
        hasBookmarkedPrompts={hasBookmarkedPrompts}
        groups={groups}
        selectedTab={selectedTab}
        groupPrompts={groupPrompts}
        onExploreOtherPrompts={onAnnouncementExploreOtherPromptsClicked}
        onSharePrompts={onAnnouncementSharePromptsClicked}
      />
    );

    const isMemberOfAnyGroup = groups && groups.length > 0;

    return (
      <>
        <WorkgroupPublisherTeachingPopover
          isEnabled={
            (isFlightActive("isWorkgroupTeachingPopoverEnabled") &&
              isMemberOfAnyGroup &&
              workgroupPublisherTeachingPopoverTarget &&
              selectedTab === "AllPrompts" &&
              isTeachingExperiencesEnabled) ||
            false
          }
          onPopoverStatus={setIsPopoverOpen}
          target={workgroupPublisherTeachingPopoverTarget}
        />
        <Toaster toasterId={toasterId} pauseOnHover pauseOnWindowBlur />

        <ShareToWorkgroupDialog
          isOpen={isShareToWorkgroupDialogOpen}
          groups={groups}
          prompts={promptsToShareToWorkgroup}
          onClose={onCloseShareToWorkgroupDialog}
          onShare={onShareToWorkgroup}
          appearance={"web"}
          isAllowMultiSelect={isFlightActive(
            "isWorkgroupPublishingMultiSelectShareEnabled"
          )}
          onGroupDropdownOptionSelect={onGroupDropdownOptionSelect}
          onGroupDropdownOpenChange={onGroupDropdownOpenChange}
        />

        <ConditionalWrap
          condition={!enableCarousel}
          wrapIfTrue={(children) => (
            <Grid
              id={id}
              role={role}
              testid="all-prompts"
              className={mergeClasses(styles.container, className)}
            >
              {children}
            </Grid>
          )}
        >
          {isShowFilters &&
            (isFlightActive("isPromptSavingEnabled") ||
              isWorkgroupPublishingEnabled) && (
              <div className={styles.promptTabRow}>
                <PromptTab
                  loading={isLoading}
                  selectedValue={selectedTab}
                  onTabSelected={handleTabChange}
                  isShowGroupTab={isWorkgroupPublishingEnabled}
                />
              </div>
            )}
          {selectedTab === "AllPrompts" && (
            <SuggestedPrompts
              isLoading={isLoading}
              groupPromptStatus={groupPromptStatus}
              currentFocusedPromptId={currentFocusedPromptId}
              enableCarousel={enableCarousel}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              prompts={prompts}
              groups={groups}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              onSharePromptClicked={onSharePromptClicked}
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onBookmarkToggled={onBookmarkToggled}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              onFilterChanged={onFilterChanged}
              setworkgroupPublisherTeachingPopoverTarget={
                setworkgroupPublisherTeachingPopoverTarget
              }
              handlePromptsTabSelected={handlePromptsTabSelected}
            >
              {announcements}
            </SuggestedPrompts>
          )}
          {selectedTab === "SavedPrompts" && (
            <BookmarkPrompts
              currentFocusedPromptId={currentFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              isLoading={isLoading}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              setLastFocusedPromptId={setLastFocusedPromptId}
              prompts={prompts}
              groups={groups}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              onSharePromptClicked={onSharePromptClicked}
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onBookmarkToggled={onBookmarkToggled}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              handlePromptsTabSelected={handlePromptsTabSelected}
            >
              {announcements}
            </BookmarkPrompts>
          )}
          {selectedTab === "GroupPrompts" && (
            <WorkgroupPrompts
              currentFocusedPromptId={currentFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              isLoading={groupPromptStatus === "loading"}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              handleCardClick={handleCardClick}
              setLastFocusedPromptId={setLastFocusedPromptId}
              prompts={prompts}
              selectedGroupId={selectedGroupId}
              groups={groups}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              onCopyPromptClicked={onCopyPromptClicked}
              isShowFilters={isShowFilters}
              onGroupPromptsFilterChanged={onGroupPromptsFilterChanged}
              handleGroupPromptsFilterOnOpenChange={
                handleGroupPromptsFilterOnOpenChange
              }
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              notify={notify}
              handlePromptsTabSelected={handlePromptsTabSelected}
            >
              {announcements}
            </WorkgroupPrompts>
          )}
        </ConditionalWrap>
      </>
    );
  }
);

AllPrompts.displayName = "AllPrompts";
